import { useMemo, useRef, useEffect, memo, useState } from "react";
import "./index.css"
import {
    ButtonGroup,
    FormCheck,
    Image,
    Spinner,
    Table,
} from "react-bootstrap";
import RowsPerPageSelect from "./RowPerPageSelect";
import { ChevronFirst, ChevronLast, ChevronLeft, ChevronRight, GripHorizontal, MoreVerticalIcon, SortAsc, SortDesc } from "lucide-react";
import { isNil } from "../../utils/otherHelopers";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import ActionDropDown from "../action-dropdown/ActionDropdown";




/* =========================
   Generic Helpers
========================= */

const get = (obj, path) => {
    if (typeof path === "function") return path(obj);
    if (!path) return undefined;
    const parts = String(path).split(".");
    let cur = obj;
    for (const p of parts) {
        if (isNil(cur)) return undefined;
        cur = cur[p];
    }
    return cur;
};

const fmt = (v) => (v == null || v === "" ? "—" : String(v));

const getRowId = (row, keyField, fallbackIndex) =>
    row?.[keyField] ?? fallbackIndex ?? String(Math.random());

const CustomTable = ({
    data = [],
    columns = [],
    loading = false,
    disabledAction = false,
    keyField = "id",
    emptyMessage = "Couldn't find any data.",
    size,
    className = "",
    rowClick = () => { },
    cover = {},
    thead = {},
    tbody = {},
    pagination = {},
    is_selectable = false,
    sortState = { id: null, dir: "asc" }, // { id: API_field OR column id, dir: 'asc'|'desc' }
    onRequestSort = () => { },
    selectedIds = [],
    onToggle = () => { },
    actionList = [],
    isRowSelectionDisabled = () => false,
    isRowActionDisabled = () => false,
    module,
    isDragable = false,
    onDragEnd = () => { },
}) => {
    const { mainClass = "", mainStyle = {} } = cover;
    const {
        className: theadClassName = "",
        style: headStyle = {},
        trClass: headTrClass = "",
        trStyle: headTrStyle = {},
        thClass = "",
        thStyle = {},
    } = thead;
    const {
        trClass: TrClass = "",
        trStyle: TrStyle = {},
        tdClass = "",
        tdStyle = {},
    } = tbody;
    const {
        currentPage = 1,
        totalPages = 0,
        total = 0,

        onPageChange = () => { },
        perPageOptions = [10, 25, 50, 100],
        pageSize = 100,
        setPageSize = () => { },
        setCurrentPage = () => { },
        // onToggleAll = null,
        className: paginationClass="",
        ...restPagination
    } = pagination;

    // ------------------------------
    // 🔹 Logic For Get Module Name
    // ------------------------------
    const [openDropdownId, setOpenDropdownId] = useState(null);
    // const { hasPermission } = useRolePermission();

    const isActionColumnVisible = useMemo(() => {
        if (disabledAction) return false;
        else return true

        // Check if at least one action is permitted
        // const canEdit = onEdit && (!module || hasPermission(module, "edit"));
        // const canDelete = onDelete && (!module || hasPermission(module, "delete"));

        // const hasPermittedOtherAction = actionList?.some(opt => {
        //     if (opt.module && opt.action) return hasPermission(opt.module, opt.action);
        //     return !opt.hidden; // Assume permitted if not hidden and no module/action specified
        // });

        // return !!(canEdit || canDelete || hasPermittedOtherAction);
    },
        [disabledAction,
            // module, onEdit, onDelete, actionList, hasPermission
        ]);

    // helper

    const pageItems = useMemo(() => {
        const items = [];
        const maxButtons = 7;
        const add = (n) =>
            items.push(
                <li
                    key={n}

                >
                    <button className={n === currentPage ? "active" : ""}
                        onClick={() => onPageChange(n)}>
                        {n}
                    </button>
                </li>,
            );

        if (totalPages <= maxButtons) {
            for (let i = 1; i <= totalPages; i++) add(i);
            return items;
        }

        const windowSize = 3;
        const left = Math.max(2, currentPage - windowSize);
        const right = Math.min(totalPages - 1, currentPage + windowSize);

        add(1);
        if (left > 2)
            items.push(<li key="l-ell" className="ellipsis">...</li>);
        for (let i = left; i <= right; i++) add(i);
        if (right < totalPages - 1)
            items.push(<li key="r-ell" className="ellipsis">...</li>);
        add(totalPages);

        return items;
    }, [currentPage, totalPages, onPageChange]);
    // display range like "11–20 of 137"
    // const startIndex = total === 0 ? 0 : (currentPage - 1) * pageSize + 1;
    // const endIndex = total === 0 ? 0 : Math.min(currentPage * pageSize, total);

    const columnsWithIds = useMemo(
        () =>
            columns.map((c, i) => ({
                sortable: c.sortable === true,
                ...c,
                _id: c.id || c.accessor || c.header || `col_${i}`,
            })),
        [columns],
    );

    const visibleRows = data;
    const isRowSelected = (rowId) => selectedIds.includes(rowId);

    const toggleRowSelection = (row) => {
        const rowId = getRowId(row, keyField);
        if (isRowSelectionDisabled(row)) return;
        onToggle(rowId);
        // setSelectedRows((prev) => {
        //   const next = new Set(prev);
        //   if (next.has(rowId)) next.delete(rowId);
        //   else next.add(rowId);
        //   return next;
        // });
    };

    const headerCheckboxRef = useRef(null);
    const selectableRows = useMemo(
        () => visibleRows.filter((row) => !isRowSelectionDisabled(row)),
        [visibleRows, isRowSelectionDisabled]
    );

    const allVisibleSelected =
        selectableRows.length > 0 &&
        selectableRows.every((row, i) => isRowSelected(getRowId(row, keyField, i)));

    const someVisibleSelected =
        selectableRows.some((row, i) => isRowSelected(getRowId(row, keyField, i))) &&
        !allVisibleSelected;

    useEffect(() => {
        if (headerCheckboxRef.current) {
            headerCheckboxRef.current.indeterminate = someVisibleSelected;
        }
    }, [someVisibleSelected]);

    // const toggleSelectAllVisible = () => {
    //     const visibleIds = selectableRows.map((row, i) =>
    //         getRowId(row, keyField, i),
    //     );
    //     if (onToggleAll) {
    //         onToggleAll(visibleIds, !allVisibleSelected);
    //     } else {
    //         // Fallback to individual toggles if onToggleAll not provided
    //         visibleIds.forEach((id) => {
    //             if (!allVisibleSelected) {
    //                 if (!selectedIds.includes(id)) onToggle(id);
    //             } else {
    //                 if (selectedIds.includes(id)) onToggle(id);
    //             }
    //         });
    //     }
    // };

    const totalColumns =
        columnsWithIds.length + (is_selectable ? 1 : 0) + (isActionColumnVisible ? 1 : 0) + (isDragable ? 1 : 0);

    const totalEntries = visibleRows.length;

    const renderTableBody = () => {
        if (loading) {
            return (
                <tbody>
                    <tr>
                        <td colSpan={totalColumns} className="text-center py-3">
                            <div
                                className="py-3 h-100 flex-center"
                                style={{ minHeight: "100px" }}
                            >
                                <Spinner animation="border" variant="primary" size="md" />
                            </div>
                        </td>
                    </tr>
                </tbody>
            );
        }

        if (totalEntries === 0) {
            return (
                <tbody>
                    <tr>
                        <td className="text-center" colSpan={totalColumns}>
                            <div className="text-center mx-auto blank-table-view">
                                <Image src={"/assets/images/table/no-data.png"} alt="Blank-Table-Data-Image" className="img-fluid" />
                                <div className="mt-3">
                                    <h5 className="fw-medium">{emptyMessage}</h5>
                                    <p className="pt-2 mt-1">
                                        This report does not contain any points. Try to change your filter
                                        and try again.
                                    </p>
                                </div>
                            </div>
                        </td>
                    </tr>
                </tbody>
            );
        }

        const rows = visibleRows.map((row, idx) => {
            const rid = getRowId(row, keyField, idx).toString();
            const renderRowContent = (provided, snapshot) => (
                <tr
                    key={rid}
                    ref={provided?.innerRef}
                    {...(provided ? provided.draggableProps : {})}
                    style={{
                        ...(provided ? provided.draggableProps.style : {}),
                        display: snapshot?.isDragging ? "table" : "table-row",
                        background: snapshot?.isDragging ? "#f8f9fa" : "transparent",
                        boxShadow: snapshot?.isDragging ? "0 5px 10px rgba(0,0,0,0.1)" : "none",
                        ...TrStyle
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        setOpenDropdownId(null);
                        rowClick(row);
                    }}
                    className={`${TrClass} ${isRowSelected(rid) ? "selected-row" : ""}`}
                >
                    {isDragable && (
                        <td
                            {...(provided ? provided.dragHandleProps : {})}
                            className="cursor-grab text-center align-middle"
                            style={{ width: "40px" }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <GripHorizontal size={16} />
                        </td>
                    )}

                    {isActionColumnVisible && (
                        <td className="" onClick={(e) => e.stopPropagation()}>
                            {!isRowActionDisabled(row) && (() => {
                                const visibleActions = actionList
                                    ?.filter(action => typeof action.isVisible === 'function' ? action.isVisible(row) : action.isVisible !== false);
                                if (!visibleActions || visibleActions.length === 0) return null;
                                return (
                                    <ActionDropDown
                                        as={ButtonGroup}
                                        id={`actions-dropdown-${rid}`}
                                        onClick={(e) => { e.stopPropagation(); }}
                                        icon={
                                            <MoreVerticalIcon
                                                size={20}
                                                color="#6D6B77"
                                            />
                                        }
                                        actions={visibleActions.map(action => ({
                                            ...action,
                                            title: typeof action.title === 'function' ? action.title(row) : action.title,
                                            handler: (e) => {
                                                e?.stopPropagation?.();
                                                if (action.handler) action.handler(row);
                                            }
                                        }))}
                                        show={openDropdownId === rid}
                                        onToggle={(isOpen) => {
                                            if (isOpen) {
                                                setOpenDropdownId(rid);
                                            } else {
                                                setOpenDropdownId(null);
                                            }
                                        }}
                                        className="action-menu"
                                        module={module}
                                    />
                                );
                            })()}
                        </td>
                    )}

                    {is_selectable && (
                        <td onClick={(e) => e.stopPropagation()}>
                            {!isRowSelectionDisabled(row) && (
                                <FormCheck
                                    type="checkbox"
                                    checked={isRowSelected(rid)}
                                    onChange={(e) => {
                                        e.stopPropagation();
                                        toggleRowSelection(row);
                                    }}
                                />
                            )}
                        </td>
                    )}

                    {columnsWithIds.map((col) => {
                        const value = col.accessor
                            ? get(row, col.accessor)
                            : undefined;
                        const content = col.cell
                            ? col.cell(value, row, idx)
                            : fmt(value);

                        if (col.isSwitch) {
                            const checked =
                                typeof col.checked === "function"
                                    ? col.checked(row)
                                    : Boolean(row[col.id]);

                            return (
                                <td
                                    key={col._id}
                                    style={{ minWidth: col.minWidth, ...tdStyle }}
                                    className={`${col?.className || ""} ${tdClass}`}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <FormCheck
                                        type="switch"
                                        id={`switch-${row[keyField]}-${col._id}`}
                                        label={col.switchLabel || ""}
                                        checked={checked}
                                        onChange={(e) => {
                                            e.stopPropagation();
                                            col.onSwitchChange?.(e.target.checked, row);
                                        }}
                                        disabled={col.isDisabled?.(row)}
                                    />
                                </td>
                            );
                        }

                        if (col.isCheckBox) {
                            return (
                                <td
                                    key={col._id}
                                    style={{ minWidth: col.minWidth, ...tdStyle }}
                                    className={`${col?.className || ""} ${tdClass}`}
                                >
                                </td>
                            );
                        }

                        if (col.isEditable) {
                            return (
                                <td
                                    key={col._id}
                                    className={`editable-cell ${col.className}`}
                                    style={{
                                        minWidth: col.minWidth,
                                        cursor: "pointer",
                                        ...tdStyle,
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        col.onEditCell({
                                            row,
                                            col,
                                        });
                                    }}
                                >
                                    {content}
                                </td>
                            );
                        }

                        return (
                            <td
                                key={col._id}
                                style={{ minWidth: col.minWidth }}
                                className={`${col.className || ""}`}
                            >
                                {content}
                            </td>
                        );
                    })}
                </tr>
            );

            if (isDragable) {
                return (
                    <Draggable key={rid} draggableId={rid} index={idx}>
                        {(provided, snapshot) => renderRowContent(provided, snapshot)}
                    </Draggable>
                );
            }

            return renderRowContent();
        });

        if (isDragable) {
            return (
                <Droppable droppableId="custom-table-droppable">
                    {(provided) => (
                        <tbody {...provided.droppableProps} ref={provided.innerRef}>
                            {rows}
                            {provided.placeholder}
                        </tbody>
                    )}
                </Droppable>
            );
        }

        return <tbody>{rows}</tbody>;
    };

    const renderTable = () => {
        const tableElement = (
            <Table
                hover
                className={`mb-0 custom-table ${className} ${visibleRows.length === 0 || loading ? "h-100" : ""}`}
                size={size}
            >
                <thead className={`${theadClassName}`} style={headStyle}>
                    <tr className={headTrClass} style={headTrStyle}>
                        {isDragable && (
                            <th style={{ width: "40px" }} />
                        )}

                        {isActionColumnVisible && (
                            <th style={{ width: "64px" }} >Actions</th>
                        )}

                        {columnsWithIds.map((col) => {
                            const isActive = sortState?.id === col.id;
                            const canSort = col.sortable === true;
                            const label =
                                typeof col.header === "string" ? (
                                    <span className="text-nowrap">{col.header}</span>
                                ) : (
                                    col.header
                                );

                            return (
                                <th
                                    key={col._id}
                                    style={{
                                        minWidth: col?.minWidth || "",
                                        maxWidth: col?.maxWidth || "",
                                        ...col.style,
                                        ...thStyle,
                                    }}
                                    className={`${col?.headerClassName || col?.className || ""} ${thClass}`}
                                    onClick={canSort ? () => onRequestSort(col.id) : undefined}
                                    role={canSort ? "button" : undefined}
                                    title={canSort ? "Sort" : undefined}
                                >
                                    <div
                                        className={` d-flex align-items-center ${col?.headerClassName?.includes("text-end")
                                            ? "justify-content-end"
                                            : "justify-content-between"
                                            }`}
                                    >
                                        {label}

                                        {canSort && (
                                            <button className="sort-btn border-0 p-0 bg-transparent ms-2 d-flex align-items-center">
                                                <SortAsc
                                                    sortDir={isActive && sortState?.dir === "asc"}
                                                />
                                                <SortDesc
                                                    sortDir={isActive && sortState?.dir === "desc"}
                                                />
                                            </button>
                                        )}
                                    </div>
                                </th>
                            );
                        })}
                    </tr>
                </thead>
                {renderTableBody()}
            </Table>
        );

        if (isDragable) {
            return (
                <DragDropContext onDragEnd={onDragEnd}>
                    {tableElement}
                </DragDropContext>
            );
        }

        return tableElement;
    };

    return (
        <div
            className={`d-flex flex-column h-100 overflow-hidden border-top position-relative rounded-8 ${mainClass}`}
            style={mainStyle}
        >
            <div className="table-responsive flex-grow-1 overflow-auto">
                {renderTable()}
            </div>

            {pagination && (totalPages >= 1 && total > 0) && (
                <div
                    className={`d-flex align-items-center gap-2 justify-content-between p-3 bg-white ${paginationClass} tbl-footer`}
                    {...restPagination}
                >
                    <span className="text-primary">{`Displaying ${total === 0 ? 0 : (currentPage - 1) * pageSize + 1}–${Math.min(
                        currentPage * pageSize,
                        total
                    )} of ${total} entries`}</span>

                    <ul className="list-unstyled custom-table-pagination mb-0 d-flex align-items-center justify-content-md-end justify-content-start">
                        <li>
                            <button className={`${currentPage === 1 || loading ? "disabled" : ""}`}
                                onClick={() => !(currentPage === 1 || loading) && onPageChange(1)}>
                                <ChevronFirst size={16} />
                            </button>
                        </li>
                        <li>
                            <button className={`${currentPage === 1 || loading ? "disabled" : ""}`}
                                onClick={() => !(currentPage === 1 || loading) && onPageChange(currentPage - 1)}>
                                <ChevronLeft size={16} />
                            </button>
                        </li>
                        {pageItems}
                        <li>
                            <button className={`${currentPage === totalPages || loading ? "disabled" : ""}`}
                                onClick={() => !(currentPage === totalPages || loading) && onPageChange(currentPage + 1)}>
                                <ChevronRight size={16} />
                            </button>
                        </li>
                        <li>
                            <button className={`${currentPage === totalPages || loading ? "disabled" : ""}`}
                                onClick={() => !(currentPage === totalPages || loading) && onPageChange(totalPages)}>

                                <ChevronLast size={16} />
                            </button>
                        </li>
                    </ul>

                    <RowsPerPageSelect
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                        setCurrentPage={setCurrentPage}
                        perPageOptions={perPageOptions}
                        loading={loading}
                    />
                </div>
            )}
        </div>
    );
};

export default memo(CustomTable);
